import { React, joinUrl, _n } from 'pexui';
import {
  camelCase, get, isArray, debounce, isObject
} from 'lodash';
import { YodleeBasePage } from './yodleebase';
import { YodleeForm } from './yodleeForm';
import { BasePage } from '../base';
import { DocUploadService } from '../../services/docuploadsvc';
import postMessageHandler from '../../services/postMessageHandler';

import globals from '../../models/globals';
import { flow, getTrackLabel } from '../../models/flow';
import { SUCCESSFUL_COMPLETED_JOURNEY_EVENT } from '../../models/consts';
import * as utils from '../../utilities/utils';
import { getDtmHierarchy } from '../../utilities/dtm';
import {
  track, trackClick, trackError, setTrackOpts, trackExit, getQuestionNumber, getIsDtm,
} from '../../utilities/track';
import AccountVerificationModal from './accountVerificationModal';
import MissingDetailsHeader from './missingDetailsHeader';

const YODLEE_LOAD_TIMEOUT = 60000; // 1 minute

export function isSSO() {
  return flow.req.is_single_sign_on || flow.req.is_sso;
}

export function getCallbackUri() {
  return joinUrl(getSsoDomain(), `yodleeresponse.html?orginatorid=${flow.req.transaction_id}`);
}

export function getSsoDomain() {
  return joinUrl(utils.getBaseUri(), 'yodlee/');
}

const updateLastNodeForYodlee = () => {
  flow.lastNodeForYodlee.done = true;

  const yodleeDocOpt = utils.findArray(
    flow.lastNodeForYodlee.widgetOption.document_types,
    docOpt => docOpt.document_option === 'Yodlee'
  );

  if (yodleeDocOpt) {
    yodleeDocOpt.done = true;
    yodleeDocOpt.completedTime = new Date();
  }
  // add a pseudo file for Yodlee result
  flow.lastNodeForYodlee.files.push({
    type: 'info',
    name: _n('Yodlee.success.message'),
    originalName: _n('Yodlee.success.message'),
    completedTime: new Date(),
  });
};

const formatYodleeResponse = (yodleeResp, isYsl) => {
  const yslResponse = {
    account_level_data: yodleeResp.sites,
    global_unique_id: yodleeResp.GUID,
    geo_location: flow.req.market,
    is_unregister: !flow.isRecurr,
  };

  const response = {
    global_unique_id: yodleeResp.GUID,
    geo_location: flow.req.market,
    is_unregister: !flow.isRecurr,
    yodlee_xref_id: yodleeResp.appID,
    segment_code: yodleeResp.Segment,
    action: yodleeResp.Action,
    snapshot_required_indicator: yodleeResp.SNAPSHOT_REQUIRED,
  };

  return isYsl ? yslResponse : response;
}

const constructResponseToInvoker = (yodleeResp, isYsl) => {
  const responseToInvoker = {
    transaction_id: flow.transaction_id,
    process_control_number: flow.req.document_data.process_control_number,
    submission_channel_code: 'YDL',
    documents_requested_code: flow.lastNodeForYodlee.code,
    received_timestamp: utils.getTimestamp(),
    yodlee_response: formatYodleeResponse(yodleeResp, isYsl),
  };

  if (
    flow.lastNodeForYodlee.account
        && flow.lastNodeForYodlee.account.customer_id
  ) {
    responseToInvoker.customer_id = flow.lastNodeForYodlee.account.customer_id;
  }

  if (
    flow.lastNodeForYodlee.account
        && flow.lastNodeForYodlee.account.organization_id
  ) {
    responseToInvoker.organization_id = flow.lastNodeForYodlee.account.organization_id;
  }

  return responseToInvoker;
};

const saveSuccessResponse = (yodleeResp, isYsl) => {
  const data = isYsl
    ? {
      guid: yodleeResp.GUID,
      account_level_data: JSON.stringify(yodleeResp.sites),
    }
    : {
      guid: yodleeResp.GUID,
      app_id: yodleeResp.appID,
      action: yodleeResp.Action,
      segment: yodleeResp.Segment,
      snapshot_required: yodleeResp.SNAPSHOT_REQUIRED,
    };

  DocUploadService.saveYodleeResponse(flow.transaction_id, data)
    .then(() => console.log('Yodlee response has been saved.'));
};

const handleError = (lastError, flowMessage, errorMessage) => {
  DocUploadService.saveYodleeResponse(flow.transaction_id, {
    error: errorMessage,
  });
  flow.lastError = lastError;
  flow.addMessage(`!${_n(flowMessage)}`, flowMessage);
};

export class YodleeSubmitPage extends YodleeBasePage {
  constructor(props) {
    super(props);
    this.timeoutHandler = null;
    this.state = {
      fgMismatchErrors: [],
      failedIdentifiers: [],
      usableIdentifiers: [],
      submitAttempts: 1,
      showConfirmationModal: false,
      yodleeResponse: null,
    };

    this.addSpinner = this.addSpinner.bind(this);
    this.handleFgMismatchError = this.handleFgMismatchError.bind(this);
    this.handleYodleeSuccess = this.handleYodleeSuccess.bind(this);
    this.messageHandler = this.messageHandler.bind(this);
    this.accountStatusMessageHandler = this.accountStatusMessageHandler.bind(this);
    this.onFrameLoad = this.onFrameLoad.bind(this);
    this.renderMissingDetailsHeader = this.renderMissingDetailsHeader.bind(this);
    this.renderConfirmationModal = this.renderConfirmationModal.bind(this);
    this.removeSpinner = this.removeSpinner.bind(this);
    this.sendResponseToInvoker = this.sendResponseToInvoker.bind(this);
    this.validateAccountDetails = this.validateAccountDetails.bind(this);
    this.infoYodleeTrackHandler = this.infoYodleeTrackHandler.bind(this);
    this.postMessageHandler = postMessageHandler((origin) => {
      const baseUri = (utils.getBaseUri()).replace(/\/$/, '');
      return origin === baseUri || /^https:\/\/finapp\.[a-z]*\.yodlee.com(?:.[a-z]{2}|)$/.test(origin);
    });
  }

  componentDidMount() {
    this.addSpinner();
    this.initializeYodleeForm();
  }

  addSpinner() {
    const yodleeIframe = document.getElementById('yodlee-iframe');
    const yodleeSpinner = (
      <div id="yodlee-spinner">
        <div className="progress-circle progress-indeterminate dls-accent-white-01-bg" />
      </div>
    );
    const existingYodleeSpinner = document.getElementById('yodlee-spinner');

    // only add spinner if one doesn't exist
    if (!existingYodleeSpinner) {
      setTimeout(() => yodleeIframe.appendChild(yodleeSpinner), 500);
    }
  }

  removeSpinner() {
    const yodleeSpinner = document.getElementById('yodlee-spinner');

    if (yodleeSpinner) {
      yodleeSpinner.parentNode.removeChild(yodleeSpinner);
      setTimeout(this.removeSpinner, 500);
    }
  }

  initializeYodleeForm() {
    this.timeoutHandler = setTimeout(() => {
      console.error('Times out accessing Yodlee page.');
      const errorKey = 'error.FG_failed.message';
      flow.addMessage(`!${_n(errorKey)}`, errorKey);
      this.props.widget.nav('index');
    }, YODLEE_LOAD_TIMEOUT);

    this.postMessageHandler.on('yodlee_success', this.messageHandler);
    this.postMessageHandler.on('yodlee_login_error', this.messageHandler);
    this.postMessageHandler.on('yodlee_session_error', this.messageHandler);
    this.postMessageHandler.on('yodlee_tech_error', this.messageHandler);
    this.postMessageHandler.on('yodlee_default_error', this.messageHandler);
    this.postMessageHandler.on({ fnToCall: 'accountStatus'}, this.accountStatusMessageHandler);
    this.postMessageHandler.on({ fnToCall: 'infoYodlee' }, this.infoYodleeTrackHandler);
    this.postMessageHandler.on({ fnToCall: 'submitAction', action: 'exit' }, data => this.validateAccountDetails(data, true));
    this.postMessageHandler.on({ fnToCall: 'ssoErrorHandle' }, (data) => {
      const { errorCode, errorSummary } = data;
      // 3rd party cookie error
      if (errorCode === 1012 || errorCode === '1012') {
        handleError(
          'sso_tech',
          'error.FG_failed.message',
          `SSO technical difficulty error. Third party cookie error. Error Params code: ${errorCode}, summary: ${errorSummary}`
        );
      } else {
        handleError(
          'sso_session',
          'Yodlee.failed.message',
          `SSO technical difficulty error. Error Params: ${errorSummary}`
        );
      }
      if (!flow.isYodleeOnly()) {
        flow.widget.nav('index');
      }
    });

    const handleDebouncedWindowSize = debounce((height) => {
      const newHeight = height > 500 ? height : 500;
      document.getElementById('yodlee-iframe').style.height = `${newHeight}px`;
    }, 550, { trailing: true });

    this.postMessageHandler.on({ fnToCall: 'newThemeResizeFloater' }, ({ height }) => {
      handleDebouncedWindowSize(height);
    });
  }

  infoYodleeTrackHandler(data) {
    const {
      category, element, page_path: pagePath, page_title: pageTitle,
    } = data;
    const formatToPascalCase = (str) => {
      const camelCaseString = camelCase(str);

      return `${camelCaseString.charAt(0).toUpperCase()}${camelCaseString.slice(1)}`;
    };

    const getBankName = (pagePath) => {
      return pagePath.split('/').length >= 4 ? pagePath.split('/')[3] : '';
    }

    const {
      transaction_id: transactionId,
      originator_id: originatorId,
      market,
    } = flow.req;

    DocUploadService.log2Server({
      transaction_id: transactionId,
      log_type: 'INFO',
      originator_id: originatorId,
      country_code: market,
      log_message: `Info event from Yodlee: ${JSON.stringify(data)}`,
    });

    if (category === 'click') {
      // format - :Element
      return trackClick(`${formatToPascalCase(element)}`);
    }

    if (category === 'page_view') {
      // format - Yodlee:PageTitle
      return track(`Yodlee:${formatToPascalCase(pageTitle)}`, { bankName: getBankName(pagePath) });
    }
    // all events have category as far as we know
    if (category) {
      // format - SomeCategory
      return track(formatToPascalCase(category));
    }
  }

  sendResponseToInvoker(responseToInvoker) {
    if (flow.isYodleeOnly()) {
      track('Yodlee:Success', { contentName: getTrackLabel(flow.lastNodeForYodlee) });
      track('ThankYou', { events: SUCCESSFUL_COMPLETED_JOURNEY_EVENT });
      flow.raiseInvokerEvent('yodleeResponse', responseToInvoker);

      if (
        typeof globals.displayMode === 'string'
        && globals.displayMode.indexOf('THANKYOU') >= 0
      ) {
        flow.widget.nav('thankyou');
      } else if ( utils.getElement('infw_btn_exit_yodlee') ) {
        utils.getElement('infw_btn_exit_yodlee').disabled = true;
      }
    } else {
      track('Yodlee:Success', { contentName: getTrackLabel(flow.lastNodeForYodlee) });
      flow.raiseInvokerEvent('yodleeResponse', responseToInvoker);
    }
  }

  handleYodleeSuccess(yodleeResp, isYsl) {
    this.addSpinner();
    const responseToInvoker = constructResponseToInvoker(yodleeResp, isYsl);

    saveSuccessResponse(yodleeResp, isYsl);
    updateLastNodeForYodlee();
    this.sendResponseToInvoker(responseToInvoker);
  }

  handleFgMismatchError(error) {
    this.setState({
      // error parameter will either be an array of error objects or a single error object
      fgMismatchErrors: isArray(error) ? error.map(i => i.message) : [error.message],
      submitAttempts: this.state.submitAttempts + 1,
    });

    if (isArray(error)) {
      return error.forEach(({ errorKey }) => trackError(errorKey));
    }

    const { errorKey } = error;

    trackError(errorKey);
  }

  handleAllAttemptsFailed(yodleeResp, isYsl) {
    // hide confirmation modal
    this.setState({ showConfirmationModal: false });
    // display spinner for yodlee only scenarios
    this.addSpinner();
    // if not just yodlee flow re-route to index and display error message
    if (!flow.isYodleeOnly()) {
      flow.addMessageToCurrentNode(_n('yodlee.fg.match.error.all.attempts'));
      flow.widget.nav('index');
    }

    const error = 'Three (3) unsuccessful attempts have been made.';
    // notify invoker with postmessage type 'fail'

    const formattedYodleeMetadata = formatYodleeResponse(yodleeResp, isYsl);
    flow.raiseInvokerEvent('fail', error, formattedYodleeMetadata);
    // save yodlee response to service
    DocUploadService.saveYodleeResponse(flow.transaction_id, { error });

    trackExit();
  }

  hasFooter() {
    return true;
  }

  messageHandler(data) {
    const messageType = utils.determineMessageType(data);
    switch (messageType) {
      case 'yodlee_success':
        const isYsl = false;
        const yodleeResp = utils.parseYodleeResponse(data.substring(15));
        return this.validateAccountDetails(yodleeResp, isYsl);
      case 'yodlee_login_error':
        handleError('sso_login', 'error.FG_failed.message', 'SSO login error');
        break;
      case 'yodlee_session_error':
        handleError('sso_session', 'error.FG_failed.message', 'SSO stale session error');
        break;
      case 'yodlee_tech_error':
        //Reading error querystring params
        let yodleeErrorResponseQueryParams;
        if (data && data.indexOf("yodlee_tech_error:" !== -1)) {
          yodleeErrorResponseQueryParams = data.substring('yodlee_tech_error:'.length);
        }
        if (yodleeErrorResponseQueryParams && /cookieDisabled=true/.test(yodleeErrorResponseQueryParams)) {
          handleError(
            'sso_tech',
            'error.FG_failed.message',
            `SSO technical difficulty error. Third party cookie error. Error Params: ${yodleeErrorResponseQueryParams}`,
            skipResponseToInvoker
          );
        } else if (yodleeErrorResponseQueryParams) {
            handleError(
              'sso_tech',
              'error.FG_failed.message',
              `SSO technical difficulty error. Error Params: ${yodleeErrorResponseQueryParams}`
            );
        } else {
          handleError(
            'sso_tech',
            'error.FG_failed.message',
            `SSO technical difficulty error.`
          );
        }
        break;
      case 'yodlee_default_error':
        handleError('sso_default', 'Yodlee.failed.message', 'SSO default error');
        break;
    }

    if (!flow.isYodleeOnly()) {
      flow.widget.nav('index');
    }
  }

  accountStatusMessageHandler(data) {
    //'accountStatus' event as a result of user submit in Fastlink 2.5 (previously used for AU and CA).
    if (isObject(data) && data.action === 'exit') {
      const isYsl = true;
      return this.handleYodleeSuccess(JSON.parse(data.sites.JSONCallbackStatus), isYsl);
    }
    //'accountStatus' event as a result of fastlink errors like login failures, failure connect to banks etc... 
    //Just logging the data. The error message for user will be displayed at Fastlink. 
    const {
      transaction_id: transactionId,
      originator_id: originatorId,
      market,
    } = flow.req;
    return DocUploadService.log2Server({
      transaction_id: transactionId,
      log_type: 'WARN',
      originator_id: originatorId,
      country_code: market,
      log_message: `Warning event from Yodlee: ${JSON.stringify(data)}`,
    });
  }

  validateAccountDetails(yodleeResp, isYsl) {
    this.addSpinner();
    // TODO - handle YSL payload with isYsl arg
    return DocUploadService
      .validateAccountDetails(yodleeResp, this.state.submitAttempts, isYsl)
      .then((res) => {
        const {
          failed_identifiers: failedIdentifiers,
          status_code: statusCode,
          usable_identifiers: usableIdentifiers,
        } = res;

        if (statusCode === 200) {
          // usableIdentifiers will only be available in the response when
          // 3 attempts have been made and at least one failed account exists
          if (usableIdentifiers) {
            return this.setState({
              failedIdentifiers,
              usableIdentifiers,
              showConfirmationModal: true,
              yodleeResponse: yodleeResp,
              submitAttempts: this.state.submitAttempts + 1,
            });
          }

          this.handleYodleeSuccess(yodleeResp, isYsl);

          if (!flow.isYodleeOnly()) {
            return flow.widget.nav('index');
          }

          return this.addSpinner();
        }

        // any failure after 3 attemps will trigger dead end scenario
        if (this.state.submitAttempts > 2) {
          return this.handleAllAttemptsFailed(yodleeResp, isYsl);
        }

        // 1. Error from FG mismatch API: Response code is null
        // 2. Error from FG mismatch API: account_level_identifiers was null
        // 3. Error from FG mismatch API: Received FG_BANKDATA_200 or FG_BANKDATA_451, but account_level_identifiers was null
        if (statusCode === 501) {
          return this.handleFgMismatchError({
            message: _n('yodlee.fg.match.error'),
            errorKey: 'yodlee.fg.match.error',
          });
        }

        // 1. Failed identifiers exist
        if (statusCode === 400) {
          if (res.failed_identifiers.length) {
            const errors = res.failed_identifiers
              .map((identifier) => {
                const errorKey = `yodlee.fg.match.error.${identifier.reject_reason_code}`;
                const errorString = _n(errorKey);
                return identifier.bank_name
                  ? {
                    message: `${identifier.bank_name}: ${errorString}`,
                    errorKey,
                  }
                  : {
                    message: `Acct No X-${identifier.account_number_ending_4_digits}: ${errorString}`,
                    errorKey,
                  };
              });

            return this.handleFgMismatchError(errors);
          }
          // render generic error if no identifiers exist
          return this.handleFgMismatchError({
            message: _n('yodlee.fg.match.error'),
            errorKey: 'yodlee.fg.match.error',
          });
        }
      }, (err) => {
        // 1. socket hangup
        // 2. responseBody.error_cd is null
        // 3. responseCode is not FG_BANKDATA_200 or FG_BANKDATA_451
        if (this.state.submitAttempts > 2) {
          return this.handleAllAttemptsFailed(yodleeResp, isYsl);
        }

        return this.handleFgMismatchError({
          message: _n('yodlee.fg.match.error'),
          errorKey: 'yodlee.fg.match.error',
        });
      });
  }

  handleAccountVerificationResponse(action) {
    this.addSpinner();
    const {
      transaction_id,
      originator_id,
      document_data,
      market: country_code,
    } = flow.req;
    const { process_control_number } = document_data;
    //Log account confirmation modal click event to server
    DocUploadService.log2Server({
      transaction_id,
      originator_id,
      process_control_number,
      country_code,
      log_type: 'INFO',
      log_message: action === 'submit'
        ? 'User confirmed to provide partially approved Yodlee data'
        : 'User declined to provide partially approved Yodlee data',
    });
    // hide account confirmation modal
    this.setState({ showConfirmationModal: false });
    const currentNode = get(flow, 'nodes', [])[0];
    // invoke success response
    if (action === 'submit') {
      const { yodleeResponse } = this.state;

      trackClick('Click_Submit', { contentName: getTrackLabel(currentNode) });
      const isYsl = !!yodleeResponse.sites;
      this.handleYodleeSuccess(yodleeResponse, isYsl);
    }
    // invoke 'failed' message to parent - BAU
    if (action === 'fail') {
      flow.raiseInvokerEvent('fail', 'User cancelled');
      trackClick('Click_Decline', { contentName: getTrackLabel(currentNode) });
    }

    // if not just yodlee flow navigate to main index
    if (!flow.isYodleeOnly()) {
      return flow.widget.nav('index');
    }
  }

  renderMissingDetailsHeader() {
    return this.state.fgMismatchErrors.length
      ? <MissingDetailsHeader fgMismatchErrors={this.state.fgMismatchErrors} />
      : null;
  }

  renderConfirmationModal() {
    track('VerifySubmit', { contentName: getTrackLabel(get(flow, 'nodes', [])[0]) });
    return (
      <AccountVerificationModal
        usableIdentifiers={this.state.usableIdentifiers}
        failedIdentifiers={this.state.failedIdentifiers}
        handleSubmit={() => this.handleAccountVerificationResponse('submit')}
        handleDecline={() => this.handleAccountVerificationResponse('fail')}
      />
    );
  }

  onFrameLoad(event) {
    if (this.timeoutHandler) {
      clearTimeout(this.timeoutHandler);
      this.timeoutHandler = null;
    }

    this.removeSpinner();
  }

  renderContent() {
    const {
      fgMismatchErrors,
      showConfirmationModal,
      submitAttempts,
      yodleeResponse,
    } = this.state;
    return (
      <div id="yodlee-submit">
        {submitAttempts <= 3 && this.renderMissingDetailsHeader()}
        {showConfirmationModal && this.renderConfirmationModal()}
        <div id="yodlee-iframe">
          <iframe
            ref="y_iframe"
            id="y_iframe"
            name="y_iframe"
            frameBorder="0"
            onLoad={this.onFrameLoad}
          />
        </div>
        {submitAttempts <= 3 && !showConfirmationModal && (
          <YodleeForm
            addSpinner={this.addSpinner}
            ssoEnabled={isSSO()}
            callbackUri={getCallbackUri()}
            ssodomain={getSsoDomain()}
            locale={flow.req.market}
            yodleeResponse={yodleeResponse}
          />
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./application/src/views/yodlee/yodleesubmit.jsx